import { useMemo } from 'react';
import { StatusBadge } from '../ui/Badge';
import { VISIT_STATUS, display } from '../status';
import { timeInTz, minutesInTz } from '../../lib/format';
import {
  dayWindow,
  hoursOf,
  isOffHour,
  layoutLane,
  dropMinute,
  type Span,
} from './timeline';

export interface TimelineVisit extends Span {
  id: string;
  status: string;
  title: string;
  customer: string;
  address: string | null;
  startAt: Date | string;
  endAt: Date | string | null;
  /** Initials, shown only when the lane's crew is bigger than one (113 decision 2). */
  initials: string | null;
  /** Why it cannot be moved, or null — the same words the server would use. */
  stuck: string | null;
}

export interface TimelineLane {
  key: string;
  name: string;
  /** `undefined` on the Unassigned lane, `null` for a tech with no van. */
  van?: string | null;
  unassigned?: boolean;
  visits: TimelineVisit[];
}

/**
 * A lane row's height, from 113 decision 3: 66px so a card's three lines — status chip and
 * time, title, address — all fit. The address is not decoration; it is how a dispatcher
 * decides whether a stop is on the way.
 */
const LANE_HEIGHT = 66;

/**
 * The height ONE track needs for a compressed (two-line) card.
 *
 * ⚠️ A lane with an overlap grows rather than dividing 66px between its tracks. Measured:
 * two tracks in a fixed lane gave each card 33px, and a two-line card is ~40px — the second
 * card was clipped off the bottom of its lane. A6's rule is that overlapping cards
 * COMPRESS, never clip, and compression has a floor.
 */
const TRACK_HEIGHT = 42;

/**
 * The dispatch board's day: rig lanes down, hours across (114 parts 2–3, design 113).
 *
 * ⚠️ The geometry lives in `./timeline`, not here. `apps/web` runs its tests in node, so
 * arithmetic inside a component is arithmetic nothing can hold to account — the window, the
 * overlap tracks and the drop minute are all pure functions with their own suite, and what
 * is left here is markup and event wiring.
 *
 * ⚠️ Everything is positioned as a FRACTION of the window, never in pixels. The board is a
 * `1fr` grid track that changes width with the sidebar, the panel and the viewport; a card
 * placed at "the 300px mark" would drift away from the hour column it belongs to the moment
 * anything else on the page resized.
 */
export function TimelineBoard({
  lanes,
  tz,
  isToday,
  nowMinute,
  selected,
  openId,
  onToggleSelect,
  onOpen,
  onOpenRig,
  onDropAt,
}: {
  lanes: TimelineLane[];
  tz: string;
  /** Whether the day being viewed IS the shop's today — the now-line's only condition. */
  isToday: boolean;
  /** Minutes since midnight at the SHOP, for the now-line. */
  nowMinute: number;
  selected: ReadonlySet<string>;
  /**
   * The visit the preview panel is currently showing.
   *
   * ⚠️ NOT the same thing as `selected`, and the two are drawn differently on purpose.
   * `selected` is the bulk-move tick — a set, and a commitment. This is where the reader's
   * attention is, one at a time, shared with the map pin so a card and its pin AGREE
   * (113/A5). Merging them would make opening a preview quietly enlist the visit in the
   * next rain-day sweep.
   */
  openId?: string | null;
  onToggleSelect: (visitId: string) => void;
  onOpen: (visitId: string) => void;
  /**
   * Open the lane's RIG — the tech, their van, and the kit on it (113/A1b's drill-down).
   *
   * ⚠️ Absent for the Unassigned lane, which is the only lane that is not a rig. A header
   * that looked clickable and did nothing there would be worse than a plain one.
   */
  onOpenRig?: (laneKey: string) => void;
  /** A card was dropped at this minute of the shop's day. */
  onDropAt: (visitId: string, minute: number) => void;
}) {
  const win = useMemo(() => dayWindow(lanes.flatMap((l) => l.visits)), [lanes]);
  const hours = useMemo(() => hoursOf(win), [win]);
  const span = win.end - win.start;
  const pct = (n: number) => `${n * 100}%`;

  /** Where in the window a pointer landed, 0–1, from the row's own box. */
  const fractionFromEvent = (e: React.DragEvent<HTMLElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return box.width > 0 ? (e.clientX - box.left) / box.width : 0;
  };

  return (
    <div className="overflow-hidden rounded-[var(--radius)] border border-border bg-surface">
      {/* The board scrolls ITSELF. `overflow-x` here rather than on the page is the rule
          A-1 made non-negotiable: the page body never scrolls sideways. */}
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* ── hour header ─────────────────────────────────────────────── */}
          <div className="flex border-b border-border bg-surface-2">
            <div className="w-[184px] shrink-0 border-r border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Technician
            </div>
            <div className="relative flex flex-1">
              {hours.map((h) => (
                <div
                  key={h}
                  className={`flex-1 border-l border-border/60 px-2 py-2 text-[11px] tabular-nums text-muted-foreground first:border-l-0 ${
                    isOffHour(h) ? 'bg-[var(--sw-muted-bg)]' : ''
                  }`}
                >
                  {/* 7am, 8am… — formatted from a real instant so the shop's locale
                      conventions apply, not a hand-rolled 12-hour string. */}
                  {formatHour(h)}
                </div>
              ))}
            </div>
          </div>

          {/* ── lanes ───────────────────────────────────────────────────── */}
          {lanes.map((lane) => {
            const placed = layoutLane(lane.visits, win);
            const tracks = Math.max(1, ...placed.map((p) => p.tracks));
            return (
              <div
                key={lane.key}
                className="flex border-b border-border last:border-b-0"
                style={{ minHeight: Math.max(LANE_HEIGHT, tracks * TRACK_HEIGHT) }}
              >
                <div
                  className={`w-[184px] shrink-0 border-r border-border px-3 py-2 ${
                    lane.unassigned ? 'bg-[var(--sw-warn-soft,transparent)]' : ''
                  }`}
                >
                  {/* min-w-0 + truncate: a long name must shrink, never widen the column
                      (a flex item's default min-width is `auto` — the 112 lesson).

                      A BUTTON when the lane is a rig, a plain div when it is not: the
                      Unassigned lane has no van and no kit, so a header that looked
                      clickable there would open a panel with nothing in it. */}
                  <HeaderShell
                    onClick={
                      onOpenRig && !lane.unassigned
                        ? () => onOpenRig(lane.key)
                        : undefined
                    }
                    label={lane.name}
                  >
                    <p
                      className={`truncate text-sm font-semibold ${
                        lane.unassigned ? 'text-[var(--sw-warn-text)]' : ''
                      }`}
                    >
                      {lane.name}
                    </p>
                    {lane.van !== undefined ? (
                      <p
                        className={`truncate text-xs ${
                          lane.van
                            ? 'text-muted-foreground'
                            : 'text-[var(--sw-warn-text)]'
                        }`}
                      >
                        {lane.van ?? 'No van assigned'}
                      </p>
                    ) : null}
                  </HeaderShell>
                </div>

                {/* The lane's own drop target. `onDragOver` must preventDefault or the
                    browser refuses the drop entirely — the drag looks alive and nothing
                    can ever land. */}
                <div
                  className="relative flex-1"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = e.dataTransfer.getData('text/visit-id');
                    if (id) onDropAt(id, dropMinute(fractionFromEvent(e), win));
                  }}
                >
                  {/* hour gridlines + off-hours tint, behind the cards */}
                  <div aria-hidden className="absolute inset-0 flex">
                    {hours.map((h) => (
                      <div
                        key={h}
                        className={`flex-1 border-l border-border/60 first:border-l-0 ${
                          isOffHour(h) ? 'bg-[var(--sw-muted-bg)]' : ''
                        }`}
                      />
                    ))}
                  </div>

                  {/* the now-line — only when the day on screen IS today */}
                  {isToday && nowMinute >= win.start && nowMinute < win.end ? (
                    <div
                      aria-hidden
                      className="absolute inset-y-0 z-10 w-px bg-[var(--sw-danger-text)]"
                      style={{ left: pct((nowMinute - win.start) / span) }}
                    />
                  ) : null}

                  {placed.map((p) => {
                    const v = p.item;
                    const s = display(VISIT_STATUS, v.status);
                    const isSel = selected.has(v.id);
                    const isOpen = v.id === openId;
                    // Overlapped cards share the lane's height, so they compress to two
                    // lines rather than clipping the address (A6).
                    const compressed = p.tracks > 1;
                    return (
                      <div
                        key={v.id}
                        className="absolute px-[2px]"
                        style={{
                          left: pct(p.left),
                          width: pct(p.width),
                          top: `${(p.track / tracks) * 100}%`,
                          height: `${(1 / tracks) * 100}%`,
                        }}
                      >
                        <div
                          className={`flex h-full min-w-0 overflow-hidden rounded-[var(--radius-sm)] border ${
                            isSel
                              ? 'border-brand bg-surface-2'
                              : 'border-border bg-surface'
                          } ${
                            /* A RING, not a border swap: the border is already spoken for
                               by the bulk-move tick, and the two states are independent —
                               a card can be both ticked and open. */
                            isOpen ? 'ring-2 ring-brand ring-offset-1' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="m-1 size-3.5 shrink-0 self-start accent-[var(--sw-brand)] disabled:opacity-40"
                            checked={isSel}
                            disabled={Boolean(v.stuck)}
                            title={v.stuck ? `Can't be moved — ${v.stuck}` : undefined}
                            aria-label={`Select ${v.title}`}
                            onChange={() => onToggleSelect(v.id)}
                          />
                          <button
                            type="button"
                            draggable={!v.stuck}
                            onDragStart={(e) => {
                              // The id travels on the EVENT, not in a ref: a ref survives
                              // a cancelled drag and the next drop uses a stale visit.
                              e.dataTransfer.setData('text/visit-id', v.id);
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onClick={() => onOpen(v.id)}
                            // The ring is not the only signal: a screen reader gets the
                            // same "this is the one the panel is describing".
                            aria-current={isOpen ? 'true' : undefined}
                            className="min-w-0 flex-1 px-1 py-1 text-left"
                            title={`${v.title} — ${v.customer}${v.address ? ` · ${v.address}` : ''}`}
                          >
                            <p className="flex items-baseline gap-1 truncate text-[11px] tabular-nums text-muted-foreground">
                              {compressed ? (
                                <span
                                  aria-hidden
                                  className="inline-block size-1.5 shrink-0 rounded-full"
                                  style={{ background: 'currentColor' }}
                                />
                              ) : (
                                <StatusBadge tone={s.tone}>{s.label}</StatusBadge>
                              )}
                              <span className="truncate">
                                {timeInTz(v.startAt, tz)}
                                {v.endAt ? `–${timeInTz(v.endAt, tz)}` : ''}
                              </span>
                              {compressed ? (
                                <span className="truncate font-medium text-[var(--sw-text)]">
                                  {v.title}
                                </span>
                              ) : null}
                            </p>
                            {compressed ? null : (
                              <p className="truncate text-xs font-medium">{v.title}</p>
                            )}
                            {/* The address is not decoration — it is how a dispatcher
                                decides whether a stop is on the way (113 decision 3). It
                                survives compression; the status chip is what gives way. */}
                            <p className="truncate text-[11px] text-muted-foreground">
                              {v.address ?? v.customer}
                              {v.initials ? ` · ${v.initials}` : ''}
                            </p>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        Drag a card along its lane to move it to a different time. Hours outside 8am–5pm
        are shaded.
      </p>
    </div>
  );
}

/**
 * The lane header's box — a real `<button>` when it opens something, a plain `<div>` when
 * it does not.
 *
 * ⚠️ A button, not a div with a click handler: Tab reaches it, Enter and Space activate it,
 * and the focus ring comes for free. The board is not allowed to be mouse-only.
 */
function HeaderShell({
  onClick,
  label,
  children,
}: {
  onClick?: () => void;
  label: string;
  children: React.ReactNode;
}) {
  if (!onClick) return <div className="min-w-0">{children}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label} — see the van and what is on it`}
      className="min-w-0 w-full rounded-[var(--radius-sm)] text-left hover:bg-surface-2"
    >
      {children}
    </button>
  );
}

/** "7am", "12pm" — from minutes since midnight at the shop. */
function formatHour(minute: number): string {
  const h = Math.floor(minute / 60) % 24;
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${suffix}`;
}

/** Minutes since midnight at the shop, for the now-line. Re-exported for the page. */
export { minutesInTz };
